import { animate, style, transition, trigger } from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  AuthService,
  CaseService,
  ErrorService,
  LanguageService,
  MediaQueryService,
  NotificationService,
} from '@/services';
import {
  CaseRating,
  CaseRatingRegistration,
  CaseRatingSystem,
  Task,
  TaskWorkgroup,
  TaskWorkgroupRegistration,
  TaskWorkgroupState,
  User,
} from '@/models';

/**
 * Precomputed per-row display data to avoid calling methods in the template
 * on every change-detection cycle (which causes lag with many rows).
 */
export interface BulkRatingRowData {
  taskWorkgroup: TaskWorkgroup;
  lastRatingId: string | null;
  stateName: string;
}

@Component({
    selector: 'app-task-bulk-rating',
    templateUrl: './task-bulk-rating.component.html',
    styleUrls: ['./task-bulk-rating.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('inOutAnimation', [
            transition(':enter', [
                style({ transform: 'translateY(200%)' }),
                animate('200ms ease-in', style({ transform: 'translateY(0%)' })),
            ]),
            transition(':leave', [
                style({ transform: 'translateY(0%)' }),
                animate('200ms ease-out', style({ transform: 'translateY(200%)' })),
            ]),
        ]),
    ],
    standalone: false
})
export class TaskBulkRatingComponent implements OnInit, OnChanges, OnDestroy {
  @Input() workgroups: TaskWorkgroup[] = [];
  @Input() task: Task;
  @Input() caseId: string;
  @Input() caseRatingSystem: CaseRatingSystem;
  @Input() taskStates: TaskWorkgroupState[] = [];

  /** Emitted when the user wants to go back to individual rating view */
  @Output() closeRequested = new EventEmitter<void>();

  displayedColumns: string[] = ['select', 'name', 'type'];
  dataSource = new MatTableDataSource<BulkRatingRowData>();
  selection = new SelectionModel<BulkRatingRowData>(true, []);
  bulkSelectionChangeDisabled = false;
  mobile = false;

  /** Sorted ratings, computed once on input change */
  sortedRatings: CaseRating[] = [];
  /** Sorted states, computed once on input change */
  sortedStates: TaskWorkgroupState[] = [];

  private mobileSubscription: Subscription;

  constructor(
    private authService: AuthService,
    private caseService: CaseService,
    private notificationService: NotificationService,
    private errorService: ErrorService,
    private languageService: LanguageService,
    private mediaQueryService: MediaQueryService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.mobileSubscription = this.mediaQueryService.mobile.subscribe((mobile: boolean) => {
      this.mobile = mobile;
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['workgroups'] || changes['task'] || changes['caseRatingSystem'] || changes['taskStates']) {
      this.rebuildColumns();
      this.rebuildDataSource();
    }
    if (changes['caseRatingSystem'] && this.caseRatingSystem?.caseRatings) {
      this.sortedRatings = [...this.caseRatingSystem.caseRatings].sort((a, b) => a.order - b.order);
    }
    if (changes['taskStates'] && this.taskStates) {
      // TaskWorkgroupState has no order property; preserve server order
      this.sortedStates = [...this.taskStates];
    }
  }

  ngOnDestroy(): void {
    this.mobileSubscription?.unsubscribe();
  }

  // ─── Table helpers ──────────────────────────────────────────────────────────

  isAllSelected(): boolean {
    return this.selection.selected.length === this.dataSource.data.length;
  }

  masterToggle(): void {
    this.isAllSelected() ? this.selection.clear() : this.dataSource.data.forEach(row => this.selection.select(row));
  }

  // ─── Per-row rating/state change ────────────────────────────────────────────

  onRatingSingleChange(row: BulkRatingRowData, event: MatSelectChange): void {
    this.onRatingChange(row, event);
  }

  onStateSingleChange(row: BulkRatingRowData, event: MatSelectChange): void {
    this.onStateChange(row, event);
  }

  // ─── Bulk-selection rating/state change ─────────────────────────────────────

  onBulkSelectionRatingChange(event: MatSelectChange): void {
    this.bulkSelectionChangeDisabled = true;
    const promises = this.selection.selected.map(row => this.onRatingChange(row, event));
    Promise.all(promises).then(() => {
      this.bulkSelectionChangeDisabled = false;
      this.cdr.markForCheck();
    });
  }

  onBulkStateSelectionChange(event: MatSelectChange): void {
    this.bulkSelectionChangeDisabled = true;
    const promises = this.selection.selected.map(row => this.onStateChange(row, event));
    Promise.all(promises).then(() => {
      this.bulkSelectionChangeDisabled = false;
      this.cdr.markForCheck();
    });
  }

  // ─── Private helpers ────────────────────────────────────────────────────────

  private rebuildColumns(): void {
    if (this.mobile) {
      this.displayedColumns = ['select', 'name', 'type'];
      return;
    }
    const cols = ['select', 'name', 'type'];
    if (this.task?.toRate) {
      cols.push('rating');
    }
    cols.push('state');
    this.displayedColumns = cols;
  }

  /**
   * Build the flat row-data array with precomputed `lastRatingId` and `stateName`
   * so the template never calls methods on every CD cycle.
   */
  private rebuildDataSource(): void {
    const rows: BulkRatingRowData[] = (this.workgroups ?? []).map(wg => this.buildRowData(wg));
    this.dataSource.data = rows;
    // Clear stale selection entries that may reference old objects
    this.selection.clear();
  }

  private buildRowData(wg: TaskWorkgroup): BulkRatingRowData {
    return {
      taskWorkgroup: wg,
      lastRatingId: this.computeLastRatingId(wg),
      stateName: wg.state?.name ?? '',
    };
  }

  private computeLastRatingId(wg: TaskWorkgroup): string | null {
    if (!wg.registrations || wg.registrations.length === 0) return null;
    const sorted = [...wg.registrations].sort((a, b) => a.submittedTimestamp - b.submittedTimestamp);
    const last = sorted[sorted.length - 1];
    return last.caseRatingRegistration?.caseRating?.id ?? null;
  }

  private onRatingChange(row: BulkRatingRowData, event: MatSelectChange): Promise<void> {
    return new Promise((resolve, reject) => {
      const rating = this.caseRatingSystem.caseRatings.find(x => x.id === event.value)!;
      const caseRatingReg = new CaseRatingRegistration();
      caseRatingReg.caseRating = rating;

      const wg = row.taskWorkgroup;

      if (wg.registrations.length > 0) {
        const sorted = [...wg.registrations].sort((a, b) => a.submittedTimestamp - b.submittedTimestamp);
        const lastReg = sorted[sorted.length - 1];
        this.caseService
          .submitTaksRegistrationRating(this.caseId, this.task.id, lastReg.id, caseRatingReg)
          .subscribe({
            next: resp => {
              // Update the registration in place and refresh row data
              const idx = wg.registrations.findIndex(r => r.id === lastReg.id);
              if (idx !== -1) wg.registrations[idx] = resp as TaskWorkgroupRegistration;
              row.lastRatingId = rating.id;
              this.notificationService.success(
                this.languageService.translateWithCap('case.tasks.notifications.rating-saved'),
              );
              this.cdr.markForCheck();
              resolve();
            },
            error: err => {
              this.errorService.onError(err);
              reject(err);
            },
          });
      } else {
        const reg = new TaskWorkgroupRegistration();
        reg.done = false;
        reg.submittedBy = new User();
        reg.submittedBy.id = this.authService.getLoggedInUserId();
        reg.functionRegistrations = [];
        this.caseService
          .submitTask(this.caseId, this.task.id, wg.workgroup.id, reg)
          .pipe(
            switchMap(resp => {
              const updated = resp as TaskWorkgroup;
              return this.caseService.submitTaksRegistrationRating(
                this.caseId,
                this.task.id,
                updated.registrations[0].id,
                caseRatingReg,
              );
            }),
          )
          .subscribe({
            next: resp => {
              wg.registrations.push(resp as TaskWorkgroupRegistration);
              row.lastRatingId = rating.id;
              this.notificationService.success(
                this.languageService.translateWithCap('case.tasks.notifications.rating-saved'),
              );
              this.cdr.markForCheck();
              resolve();
            },
            error: err => {
              this.errorService.onError(err);
              reject(err);
            },
          });
      }
    });
  }

  private onStateChange(row: BulkRatingRowData, event: MatSelectChange): Promise<void> {
    return new Promise((resolve, reject) => {
      const tws = new TaskWorkgroupState();
      tws.name = event.value;
      this.caseService
        .updateTaskWorkgroupState(this.caseId, this.task.id, row.taskWorkgroup.workgroup.id, tws)
        .subscribe({
          next: resp => {
            row.taskWorkgroup.state = (resp as TaskWorkgroup).state;
            row.stateName = row.taskWorkgroup.state.name;
            this.notificationService.success(this.languageService.translateWithCap('state-change-saved'));
            this.cdr.markForCheck();
            resolve();
          },
          error: err => {
            this.errorService.onError(err);
            reject(err);
          },
        });
    });
  }
}
